import type { Gestures } from './gestures';
import type { MapPreset } from './map-toggle';

/** One line of a popup's fact list ("Räder frei" → "7"). */
export interface PinFact { label: string; value: string }
/** One link of a popup's footer ("In der App öffnen" → deep link). */
export interface PinLink { label: string; url: string }

/** Everything the widget draws on the map for ONE assistant answer. */
export interface MapPin {
  id: string;
  kind: 'poi' | 'place' | 'stop';
  name: string;
  lat: number;
  lon: number;
  address?: string;
  description?: string;
  openingHours?: string;
  price?: string;
  image?: string;
  source?: string;
  /** DHID or other main id, when the pin is a stop/place */
  ref?: string;
  /** canonical entdecke-nds.de entity page — only when the answer carried `hub_detail_url`
   *  (Sprint 749: built + HEAD-checked in the tourism middleware for NDS-Hub POIs).
   *  The widget never constructs this itself: an unchecked link could be a 404. */
  hubUrl?: string;
  /** what the tool said about this place, beyond name and address — availability, distance, operator … */
  facts?: PinFact[];
  /** other links the tool carried (app deep link, operator website, OpenStreetMap) */
  links?: PinLink[];
  /** the line of the answer that talks about this place (markdown), so the popup says at least what the text said */
  quote?: string;
}

export interface MapLegNode {
  name: string;
  ref?: string;
  lat?: number;
  lon?: number;
  time?: string;
  /** when the vehicle leaves this halt: the timetable's instant, then the live one when the router had it (ISO) */
  departs?: string[];
  /** when it reaches this halt, the same way */
  arrives?: string[];
  platform?: string;
}

export interface MapLeg {
  journeyIndex: number;
  legIndex: number;
  line: string;
  mode: string;
  operator?: string;
  direction?: string;
  nodes: MapLegNode[];
  /** polyline when the answer carried one (`pathOnMap`, needs the `extended` tool flag), else empty */
  path: [number, number][];
  /** who drew `path`: `geo` = the geo team's real track, `otp` = the router's chain of halts
   *  (Sprint 748, field `pathSource`); absent on answers from older servers */
  pathSource?: string;
  /** papi's own word for how it drew `path` (`additionalInfos.course`, papi Sprint 972/1059): `matched` /
   *  `shape` / `street` = on the network (a real track), `stops` = the chain of halts; absent from other routers */
  course?: string;
  /** the line colour geo came with (`pathColour`), when it did */
  colour?: string;
}

/** A section the map draws: a leg as the answer tells it — its halts from the boarding to the
 *  alighting the text names, its course cut to them — or, faint, a leg of an alternative the text
 *  names as one. */
export interface RouteLeg extends MapLeg {
  alternative: boolean;
}

/** A drawn course that is not part of a journey: a tour's route, a ring line's loop. */
export interface MapTrack {
  id: string;
  name: string;
  /** [lon, lat] */
  path: [number, number][];
  source?: string;
  ref?: string;
}

export interface MapModel {
  pins: MapPin[];
  legs: MapLeg[];
  tracks: MapTrack[];
  /** the journey the answer text recommends (its lines are the ones the text names); 0 when unclear */
  primaryJourney: number;
  /** the journeys the map draws: the recommended one plus its continuation SEGMENTS (a separate
   *  connections call for the onward bike/bus ride) — but NOT its ALTERNATIVES (a second A→B option
   *  that shares both endpoints). Empty falls back to [primaryJourney]. */
  drawnJourneys: number[];
  /** what the map draws of the journeys: the trip the text tells, out of the recommended journey and
   *  its continuations, then the alternatives the text names (extract.ts::routeOf) */
  route: RouteLeg[];
  /** [minLon, minLat, maxLon, maxLat] over everything with coordinates */
  bbox: [number, number, number, number] | null;
}

export const emptyModel = (): MapModel => ({ pins: [], legs: [], tracks: [], primaryJourney: 0, drawnJourneys: [], route: [], bbox: null });

export interface ToolCall {
  callId: string;
  name: string;
  arguments: Record<string, unknown>;
}

export interface ToolResult {
  callId: string;
  name: string;
  text: string;
}

export interface AssistantAnswer {
  text: string;
  calls: ToolCall[];
  results: ToolResult[];
  /** what the model is doing right now, while not yet done */
  running: string[];
  done: boolean;
}

/** Why a question got no answer: the service could not be reached, failed, turned the widget away
 *  (key or path not accepted), had too many requests, refused the request, went quiet, sent something
 *  unreadable — or the widget has no key to ask with. */
export type ErrorKind = 'network' | 'server' | 'unavailable' | 'busy' | 'refused' | 'timeout' | 'unexpected' | 'setup';

export interface TurnError {
  kind: ErrorKind;
  /** the technical detail, for the console and the turn's `data-detail` — never shown as text */
  detail: string;
  /** the question that failed, while asking it again is offered */
  question?: string;
}

export interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** assistant only */
  answer?: AssistantAnswer;
  map?: MapModel;
  createdAt: number;
  error?: TurnError;
}

export interface WidgetConfig {
  baseUrl: string;
  apiKey: string;
  model: string;
  toolIds: string[];
  tilesUrl: string;
  /** append the hint that asks the model for coordinates (needs the `extended` tool flag) */
  extendedHint: boolean;
  /** delete the OpenWebUI chat after the answer arrived */
  deleteChats: boolean;
  pollMs: number;
  timeoutMs: number;
  title: string;
  placeholder: string;
  /** the placeholder where the long one does not fit the input */
  placeholderShort: string;
  /** EU-KI-Verordnung Art. 50: der sichtbare Hinweis, dass hier eine KI antwortet. Leerer String schaltet ihn ab. */
  aiNotice: string;
  /** Die Kurzform für einen schmalen Chat (bis 440 px), eine Zeile auf dem Handy; dort entfällt die Fußzeile. */
  aiNoticeShort: string;
  /** Optionale Adresse zum Hinweis — z. B. die Hinweisseite des Betreibers: hinter „Mehr dazu\" nach dem vollen Hinweis; in einem schmalen Chat ist die Kurzform selbst der Link. */
  aiNoticeHref: string;
  /** questions a visitor can ask with one tap while the history is empty (attribute `examples`, „|“-separated) */
  examples: string[];
  /** initial map view when nothing is drawn yet */
  home: { lat: number; lon: number; zoom: number };
  /** the host's preset for the map (attribute `map`); see `resolveMapShown` for how it meets the visitor's choice */
  map: MapPreset;
  /** how one finger acts on the map (attribute `gestures`) */
  gestures: Gestures;
  /** show the button that hides and shows the map (attribute `map-toggle`) */
  mapToggle: boolean;
}
